import React, { useEffect, useState } from "react";
import useAxios from "../../hooks/useAxios";
import { useSelector } from "react-redux";
import ApproveTable from "../../components/Table/ApproveTable";
import {
  formatIsoDate,
  renderStatus,
  formatName,
  formatPositionLabel,
  toUiPosition,
} from "../../utils/generalUtils";
import GeneratePdf from "../../generatePdf/GeneratePdf";
import { Button, Select, Space } from "antd";
import PivotTable from "../../components/Table/PivotTable";
import TableSkeleton from "../../components/Loading/TableSkeleton";

import { EyeOutlined } from "@ant-design/icons";

const ApproveTRLocal = () => {
  const { response, loading, fetchData } = useAxios();
  const { approverTrlPopup } = useSelector((state) => state.generalSlice);
  const { positions, id: employee_id } = useSelector(
    (state) => state.accountDetailSlice.details
  );

  const handleRefresh = (positionId) => {
    fetchData({ url: `/approver/trl/${positionId}`, method: "GET" });
  };

  const mappedEmpPos = positions
    .filter((item) => item?.is_approver)
    .map((item) => {
      const normalized = toUiPosition(item);

      return {
        value: normalized?.position_id,
        label: formatPositionLabel(normalized),
        disabled: normalized?.is_inactive,
      };
    });
  const activeEmpPos = mappedEmpPos.filter((item) => !item.disabled);
  const hasActiveApproverPosition = activeEmpPos.length > 0;

  const [selectedPos, setSelectedPos] = useState(
    activeEmpPos.length > 0 ? activeEmpPos[0].value : undefined
  );

  useEffect(() => {
    if (
      (selectedPos === undefined || selectedPos === null) &&
      activeEmpPos.length > 0
    ) {
      setSelectedPos(activeEmpPos[0].value);
    }
  }, [activeEmpPos, selectedPos]);

  const handlePosSelect = (posId) => {
    setSelectedPos(posId);
  };

  useEffect(() => {
    if (selectedPos === undefined || selectedPos === null) {
      return;
    }

    handleRefresh(selectedPos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPos]);

  const mappedData =
    response?.data?.map((item, index) => ({
      key: index + 1,
      name: `${item?.submission?.submitter?.last_name || ""}, ${
        item?.submission?.submitter?.first_name || ""
      }`.trim(),
      formattedName: formatName(
        item?.submission?.submitter?.first_name,
        item?.submission?.submitter?.middle_name,
        item?.submission?.submitter?.last_name,
        "lfmcut"
      ),
      approver_name: `${item?.approver?.last_name || ""}, ${
        item?.approver?.first_name || ""
      }`.trim(),
      formattedApproverName: formatName(
        item?.approver?.first_name,
        item?.approver?.middle_name,
        item?.approver?.last_name,
        "lfmcut"
      ),
      ...item?.submission?.travel_request_local,
      date_submitted: item?.submission?.created_at
        ? formatIsoDate(item?.submission?.created_at)
        : null,
      submitter_position: item?.submission?.position?.position_name,
      remarks: item?.submission?.remarks || null,
      status: item?.status || "no data",
      submission_id: item?.submission?.id,
      approval_id: item?.id,
      approval_date: item?.approval_date,
    })) || [];

  //columns for table
  const columns = [
    {
      title: "No.",
      dataIndex: "key", width: 60,
      rowScope: "row",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Position",
      dataIndex: "submitter_position",
      key: "submitter_position",
    },
    {
      title: "Destination",
      dataIndex: "destination",
      key: "destination",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => {
        return (
          <>
            <GeneratePdf
              docuType="trl"
              contents={item}
              disabled={item?.status !== "approved"}
              isIcon={true}
            />
            <Button icon={<EyeOutlined />} type="primary" shape="circle" />
          </>
        );
      },
    },
  ];

  //this is optional if there is a additional form other than approving and disapproving just like remarks
  const addtnlfield = [
    {
      name: "remarks",
      label: "Send Remarks",
      placeholder: "Enter remarks",
    },
  ];
  //   {
  //     key: "1",
  //     label: "Required Details",
  //     detailItems: [
  //       {
  //         name: "name",
  //         label: "Requester",
  //         disabled: true,
  //       },
  //       {
  //         name: "start_date",
  //         label: "Start Date",
  //         disabled: true,
  //       },
  //       {
  //         name: "end_date",
  //         label: "End Date",
  //         disabled: true,
  //       },
  //       {
  //         name: "destination",
  //         label: "Destination",
  //         disabled: true,
  //       },
  //       {
  //         name: "purpose",
  //         label: "Purpose of Travel",
  //         type: "textarea",
  //         disabled: true,
  //       },
  //     ],
  //   },
  //   {
  //     key: "2",
  //     label: "Additional Details",
  //     detailItems: [
  //       {
  //         name: "expenses",
  //         label: "Estimated Expenses",
  //         disabled: true,
  //       },
  //       {
  //         name: "funding_sourse",
  //         label: "Funding Source",
  //         disabled: true,
  //       },
  //       {
  //         name: "local_travel_transportation",
  //         label: "Local Travel Transportation",
  //         disabled: true,
  //       },
  //       {
  //         name: "entitlement",
  //         label: "Entitlement",
  //         disabled: true,
  //       },
  //     ],
  //   },
  // ];

  const viewingDataConfig = [
    {
      //  NOTE: MAKE THE KEY TO 1 IN SENDING THE PROPS IF YOU WANT IT ACTIVE, 0 OTHERWISE
      key: 1,
      collapseName: "Required Details",
      collapseItems: [
        {
          key: 1,
          name: "name",
          label: "Requester",
        },
        {
          key: 2,
          name: "start_date",
          label: "Start Date",
        },
        {
          key: 3,
          name: "end_date",
          label: "End Date",
        },
        {
          key: 4,
          name: "destination",
          label: "Destination",
        },
        {
          key: 5,
          name: "purpose",
          label: "Purpose of Travel",
        },
      ],
    },
    {
      //  NOTE: MAKE THE KEY TO 1 IN SENDING THE PROPS IF YOU WANT IT ACTIVE, 0 OTHERWISE
      key: 1,
      collapseName: "Additional Details",
      collapseItems: [
        {
          key: 1,
          name: "expenses",
          label: "Estimated Expenses",
        },
        {
          key: 2,
          name: "funding_sourse",
          label: "Funding Source",
        },
        {
          key: 3,
          name: "local_travel_transportation",
          label: "Local Travel Transportation",
        },
        {
          key: 4,
          name: "entitlement",
          label: "Entitlement",
        },
      ],
    },
  ];

  return (
    <>
      {loading ? (
        <TableSkeleton tableType="approve" />
      ) : (
        <>
          <Space>
            <p>Approve as: </p>
            <Select
              value={selectedPos}
              options={mappedEmpPos}
              onChange={handlePosSelect}
              disabled={!hasActiveApproverPosition}
            />
          </Space>
          {!hasActiveApproverPosition && (
            <p style={{ marginTop: 8, color: "#8c8c8c" }}>
              No active approver position available.
            </p>
          )}

          <ApproveTable
            tableColumn={columns}
            tableData={mappedData}
            popupSlice={approverTrlPopup}
            popupName="approverTrlPopup"
            docuType="Local Travel Request"
            addtnlFormFields={addtnlfield}
            viewingDataConfig={viewingDataConfig}
            approverId={employee_id}
            approverposId={selectedPos}
            onRefresh={() => {
              if (selectedPos !== undefined && selectedPos !== null) {
                handleRefresh(selectedPos);
              }
            }}
          />
        </>
      )}
    </>
  );
};

export default ApproveTRLocal;
